export type TelephonyProvider = 'RINGCENTRAL' | 'VICIDIAL'

export interface TelephonyDrawerDiagnostics {
  message?: string
  agent_user?: string
  status?: string
  call_id?: string
  campaign_id?: string
  pause_code?: string
  session_id?: string
  phone_number?: string
  vendor_lead_code?: string
  real_time_sub_status?: string
  raw_response?: string
}

export interface TelephonyResolveResponse {
  provider: TelephonyProvider
  telephony_provider: TelephonyProvider
  source: string
  vicidial_enabled: boolean
  vicidial_base_url: string
  vicidial_agent_user: string
  vicidial_phone_login: string
  vicidial_campaign_id: string
  vicidial_auto_login_configured: boolean
  vicidial_webphone_configured: boolean
  vicidial_supervisor_access: boolean
  ringcentral_enabled: boolean
}

export interface TelephonyCampaignOption {
  campaign_id: string
  campaign_name: string
  active: string
}

export interface TelephonyInboundGroupOption {
  group_id: string
  group_name: string
  active?: string
}

export interface TelephonyWebphoneResponse {
  provider: TelephonyProvider
  url: string
  source: 'api' | 'config' | 'autologin'
  display_mode?: 'webphone' | 'agent'
  warning?: string
  opened_externally?: boolean
}

export interface TelephonyStartResponse {
  provider: TelephonyProvider
  call_id?: number | null
  lead_id?: number | null
  agent_user?: string
  normalized_phone?: string
  vendor_lead_code?: string
  first_name?: string
  last_name?: string
  email?: string
  raw_response?: string
  external_call_id?: string
  session_status?: {
    status?: string
    campaign_id?: string
    session_id?: string
    phone_number?: string
  }
  session_raw_response?: string
}

export interface TelephonyHangupResponse {
  provider: TelephonyProvider
  call_id?: number | null
  raw_response?: string
}

export interface TelephonyStatusResponse {
  provider: TelephonyProvider
  agent_user?: string
  active_session: boolean
  raw_response?: string
  status?: string
  call_id?: string
  policytools_call_id?: number | null
  policytools_call_completed?: boolean
  lead_id?: string
  campaign_id?: string
  pause_code?: string
  real_time_sub_status?: string
  phone_number?: string
  vendor_lead_code?: string
  session_id?: string
  first_name?: string
  last_name?: string
  email?: string
  calls_in_queue_count?: number
}

export interface TelephonyVicidialActionResponse extends TelephonyStatusResponse {
  action?: string
  dtmf_digits?: string
  pause_code_requested?: string
  ingroups?: string[]
  disposition_code?: string
  callback_datetime?: string
  callback_type?: string
  dial_queue?: TelephonyDialQueueSummary | null
  dial_queue_advanced?: boolean
}

export interface TelephonyDialQueueItemSummary {
  id: number
  lead_id: number
  lead_name: string
  phone: string
  position: number
  status: string
  attempts: number
  call_id: number | null
  disposition_code: string
  last_error: string
  started_at: string | null
  completed_at: string | null
}

export interface TelephonyDialQueueCounts {
  total: number
  pending: number
  dialing: number
  completed: number
  failed: number
  skipped: number
  canceled: number
}

export interface TelephonyDialQueueSummary {
  id: number
  status: 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'STOPPED'
  provider: TelephonyProvider
  campaign_id: string
  last_error: string
  next_dial_at: string | null
  created_at: string | null
  started_at: string | null
  finished_at: string | null
  counts: TelephonyDialQueueCounts
  current_item: TelephonyDialQueueItemSummary | null
}

export interface TelephonyDialQueueResponse {
  queue: TelephonyDialQueueSummary | null
  started?: boolean
}

export interface TelephonyDisconnectResponse {
  provider: TelephonyProvider
  agent_user?: string
  raw_response?: string
  hangup_raw_response?: string
  active_session: boolean
  status?: string
  campaign_id?: string
  session_id?: string
  phone_number?: string
  status_before_disconnect?: string
}

export interface TelephonyDrawerOpenDetail {
  url: string
  provider?: TelephonyProvider
  title?: string
  warning?: string
  width?: string
  displayMode?: 'webphone' | 'agent'
  diagnostics?: TelephonyDrawerDiagnostics
}

export interface TelephonyDrawerStateDetail {
  mounted: boolean
  visible: boolean
  provider?: TelephonyProvider
}

export interface TelephonyVicidialSessionDetail {
  connected: boolean
  connecting: boolean
  activeSession: boolean
  agentUser: string
  phoneLogin: string
  selectedCampaignId: string
  status: string
  callId: string
  localCallId: number | null
  leadId: string
  campaignId: string
  pauseCode: string
  realTimeSubStatus: string
  sessionId: string
  phoneNumber: string
  vendorLeadCode: string
  firstName: string
  lastName: string
  email: string
  callsInQueueCount: number
  callStartedAt: number | null
  wrapUpStartedAt: number | null
  message: string
  rawResponse: string
  checkedAt: number | null
}

export interface TelephonyVicidialDispositionDetail {
  key: string
  status: string
  realTimeSubStatus: string
  callId: string
  leadId: string
  campaignId: string
  phoneNumber: string
  vendorLeadCode: string
  firstName: string
  lastName: string
  email: string
  sessionId: string
  checkedAt: number | null
}

export interface TelephonyVicidialPrepareDialDetail {
  phone: string
  leadId?: number | string | null
  autoDial?: boolean
}

export const TELEPHONY_DRAWER_OPEN_EVENT = 'telephony:open-drawer'
export const TELEPHONY_DRAWER_UPDATE_EVENT = 'telephony:update-drawer'
export const TELEPHONY_DRAWER_CLOSE_EVENT = 'telephony:close-drawer'
export const TELEPHONY_DRAWER_TOGGLE_EVENT = 'telephony:toggle-drawer'
export const TELEPHONY_DRAWER_STATE_EVENT = 'telephony:drawer-state'
export const TELEPHONY_VICIDIAL_LOGOUT_EVENT = 'telephony:vicidial-logout'
export const TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT = 'telephony:vicidial-prepare-dial'
export const TELEPHONY_VICIDIAL_SESSION_EVENT = 'telephony:vicidial-session'
export const TELEPHONY_VICIDIAL_DISPOSITION_EVENT = 'telephony:vicidial-disposition'
